/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { credentialsKey } from '@app/core/authentication/authentication.service';
import { environment } from '@env/environment';

/**
 * Adds a default error handler to all requests.
 */
@Injectable()
export class ErrorHandlerInterceptor implements HttpInterceptor {
  constructor(private toastr: ToastrService, private router: Router) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(catchError((error) => this.errorHandler(error)));
  }

  // Customize the default error handler here if needed
  private errorHandler(response: HttpEvent<any>): Observable<HttpEvent<any>> {
    if (!environment.production) {
      // Do something with the error
      // log.error('Request error', response);
    }

    if (response instanceof HttpErrorResponse) {
      if (response.status === 422 && response.error.errors) {
        Object.keys(response.error.errors).forEach((key: string) => {
          this.toastr.error(response.error.errors[key], response.error.message);
        });
      } else if (response.status === 422 && response.error instanceof Blob) {
        const blob = new Blob([response.error], {
          type: response.error.type,
        });

        const reader = new FileReader();

        reader.onload = (result: any) => {
          const res = JSON.parse(result.target.result);
          res.errors.forEach((err: string) => {
            this.toastr.error(err);
          });
        };
        reader.readAsText(blob);
      } else if (response.status === 400) {
        this.toastr.error(response.error.message, 'Error 400');
      } else if (response.status === 429) {
        this.toastr.error(response.message, 'Error 429');
      } else if (response.status === 413) {
        this.toastr.error('Payload Too Large', 'Error 413');
      } else if (response.status === 401) {
        let needReLogin = false;

        if (response.error && response.error.errors) {
          response.error.errors.forEach((value: string) => {
            if (value === 'Unauthenticated.') {
              needReLogin = true;
            }

            this.toastr.error(value, 'Error 401');
          });
        } else if (response.error && response.error.message) {
          this.toastr.error(response.error.message, 'Error 401');
        } else {
          this.toastr.error('Unknown error', 'Error 401');
        }

        if (needReLogin) {
          // logout
          sessionStorage.removeItem(credentialsKey);
          localStorage.removeItem(credentialsKey);

          // redirect to login
          this.router.navigateByUrl('login');
        }
      } else if (response.status === 404) {
        this.toastr.error('Not found', 'Error 404');
      } else if (response.status === 500) {
        if (response.error && response.error.errors) {
          response.error.errors.forEach((value: string) => {
            this.toastr.error(value, 'Server error 500');
          });
        } else {
          this.toastr.error('Unknown 500 error', 'Server error 500');
        }
      }
    }

    throw response;
  }
}
